import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useRetainedValue } from '~hooks/useRetainedValue';
import { usePathname, useRouter } from '~i18n/navigation';
import RandomIcon from '~public/assets/icons/random.svg';
import UserIcon from '~public/assets/icons/user.svg';
import { InputField } from '~shared/ui/organisms/InputField';
import { getRandomInt } from '~utils/numbers';
import { updateQueryParamsWithPathname } from '~utils/searchParams';

import type {
	BotWizardFieldNames,
	BotWizardFieldText,
	BotWizardValues,
} from '../../model/types';
import { getDependencyContent } from '../../model/utils';

export const FieldText = ({
	data,
	contentDependency,
	values,
	queryKey,
	nameError,
	disabled,
	id,
}: BotWizardFieldText & {
	id: BotWizardFieldNames;
	values: BotWizardValues;
	nameError?: string;
	disabled?: boolean;
}) => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const localData = getDependencyContent({
		data,
		contentDependency,
		values,
	});
	const [randomIndex, setRandomIndex] = useState(
		getRandomInt(0, localData?.length ?? 0)
	);

	const [value, setValue] = useRetainedValue(
		(values[id] as string) || '',
		(v) => {
			router.push(
				updateQueryParamsWithPathname({ [queryKey]: v }, pathname, searchParams)
			);
		}
	);

	const handleRandom = () => {
		if (!localData) {
			return;
		}
		let newIndex = randomIndex + 1;
		if (newIndex >= localData.length) {
			newIndex = 0;
		}
		setRandomIndex(newIndex);
		setValue(localData[newIndex]);
	};

	useEffect(() => {
		if (!values[id]) {
			requestAnimationFrame(() => {
				handleRandom();
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div>
			<InputField
				maxLength={20}
				leftIcon={<UserIcon />}
				disabled={disabled}
				rightIcon={
					<button disabled={disabled}>
						<RandomIcon />
					</button>
				}
				value={value}
				onChange={(e) => {
					setValue(e.target.value);
				}}
				onRightIconClick={handleRandom}
				uiSize="L"
				error={nameError}
			/>
		</div>
	);
};
